import React, { useContext, useMemo } from 'react';
import CardBox from '../../../CardBox';
import styles from './index.module.scss';
import ReactEcharts from 'echarts-for-react';
import { getPieChartOption } from './chart';
import { useRequest } from 'ahooks';
import { formatCarbon, formatElectricity, formatPower, keepTwoOrLine } from '@/pages/IndustrialGreenMicBigScreen/utils';
import { apiV2MicrogridOverviewGetSocialBenefitPost } from '@maxtropy/intelligent-dispath-apis-v2';
import { isNil } from 'lodash-es';
import { ShareDataContext } from '@/pages/IndustrialGreenMicBigScreen';

const SocialBenefitsOfNewEnergy = () => {
  const { microgridId, dataExit } = useContext(ShareDataContext);
  const { data } = useRequest(() => apiV2MicrogridOverviewGetSocialBenefitPost({ id: microgridId }), {
    ready: !!microgridId,
    refreshDeps: [microgridId],
    pollingInterval: 1000 * 60 * 15,
  });

  const getPercentage = (num1?: number, num2?: number) => {
    if (isNil(num1) || isNil(num2)) {
      return '--';
    }
    const sum = num1 + num2;
    return sum === 0 ? '--' : ((num1 / sum) * 100).toFixed(2);
  };

  return (
    <CardBox title="新能源社会效益">
      <div className={styles.container}>
        <div className={styles.topRow}>
          <div className={styles.chartBox}>
            <ReactEcharts
              style={{ height: 56, width: 56 }}
              option={getPieChartOption(
                [
                  { name: '电网月供电', num: data?.monthlyGridSupply ?? 0 },
                  { name: '新能源月消纳', num: data?.monthlyNewEnergyUtilization ?? 0 },
                ],
                false
              )}
              notMerge
              lazyUpdate={false}
            />
            <div>
              <div className={styles.label}>
                电网月供电({getPercentage(data?.monthlyGridSupply, data?.monthlyNewEnergyUtilization)}%)
              </div>
              <div className={styles.num}>{formatElectricity(data?.monthlyGridSupply)}</div>

              <div className={styles.label}>
                新能源月消纳({getPercentage(data?.monthlyNewEnergyUtilization, data?.monthlyGridSupply)}%)
              </div>
              <div className={styles.num}>{formatElectricity(data?.monthlyNewEnergyUtilization)}</div>
            </div>
          </div>
          <div className={styles.splitVerticalLine}></div>
          <div className={styles.chartBox}>
            <ReactEcharts
              ref={d => {}}
              style={{ height: 56, width: 56 }}
              option={getPieChartOption(
                [
                  { name: '电网年供电', num: data?.annualGridSupply ?? 0 },
                  { name: '新能源年消纳', num: data?.annualNewEnergyUtilization ?? 0 },
                ],
                true
              )}
              notMerge
              lazyUpdate={false}
            />
            <div>
              <div className={styles.label}>
                电网年供电({getPercentage(data?.annualGridSupply, data?.annualNewEnergyUtilization)}%)
              </div>
              <div className={styles.num}>{formatElectricity(data?.annualGridSupply)}</div>

              <div className={styles.label}>
                新能源年消纳({getPercentage(data?.annualNewEnergyUtilization, data?.annualGridSupply)}%)
              </div>
              <div className={styles.num}>{formatElectricity(data?.annualNewEnergyUtilization)}</div>
            </div>
          </div>
        </div>
        <div className={styles.dataBoxContainer}>
          <div className={styles.middleRow}>
            <div className={styles.itemBox}>
              <div className={styles.title}>新能源年度统计</div>
              <div className={styles.dataContent}>
                <div className={styles.row}>
                  <div className={styles.desc}>年发电量({formatPower(data?.annualNewEnergyGeneration).unit})</div>
                  <div className={styles.keyValue}>{formatPower(data?.annualNewEnergyGeneration).value}</div>
                </div>
                <div className={styles.splitLine}></div>
                <div className={styles.row}>
                  <div className={styles.desc}>年上网量({formatPower(data?.annualNewEnergyFeedIn).unit})</div>
                  <div className={styles.keyValue}>{formatPower(data?.annualNewEnergyFeedIn).value}</div>
                </div>
              </div>
            </div>
            <div className={styles.itemBox}>
              <div className={styles.title}>新能源年有效小时数</div>
              <div className={styles.dataContent}>
                <div className={styles.row}>
                  <div className={styles.desc}>光伏(h)</div>
                  <div className={styles.keyValue}>{keepTwoOrLine(data?.annualPvEffectiveHours)}</div>
                </div>
                {dataExit?.wpExists && (
                  <>
                    <div className={styles.splitLine}></div>
                    <div className={styles.row}>
                      <div className={styles.desc}>风电(h)</div>
                      <div className={styles.keyValue}>{keepTwoOrLine(data?.annualWpEffectiveHours)}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={styles.bottomRow}>
            <div className={styles.itemContent}>
              <div className={styles.desc}>等效植树(棵)</div>
              <div className={styles.keyValue}>{keepTwoOrLine(data?.equivalentTreesPlanted)}</div>
            </div>
            <div className={styles.itemContent}>
              <div className={styles.desc}>年减排({formatCarbon(data?.annualEmissionReduction).unit})</div>
              <div className={styles.keyValue}>{formatCarbon(data?.annualEmissionReduction).value}</div>
            </div>
            <div className={styles.itemContent}>
              <div className={styles.desc}>节约标准煤(t)</div>
              <div className={styles.keyValue}>{keepTwoOrLine(data?.annualStandardCoalSaved)}</div>
            </div>
          </div>
        </div>
      </div>
    </CardBox>
  );
};

export default SocialBenefitsOfNewEnergy;
